import React from 'react'
import styled from 'styled-components'
import Box, {BoxProps} from '../Box'
import sx, {SxProp} from '../../sx'
import {themeGet} from '../../utils/theme-get'
import {useCenterY} from './hooks'
import {VerticalPositionProvider} from './context/VerticalPositionContext'
import {RuleBuilderLayoutProps} from './types'

const GridAreas = {
  Left: 'left',
  Right: 'right',
  Header: 'head',
  Footer: 'foot'
}

// --------------------------------------------------------
// Root
const RuleBuilderLayout = ({children, isSplitPane = false, sx: sxProps, ...props}: RuleBuilderLayoutProps) => {
  return (
    <VerticalPositionProvider>
      <Box
        sx={{
          ...sxProps,
          display: 'grid',
          gridTemplateAreas: `"${GridAreas.Header} ${GridAreas.Header}" "${GridAreas.Left} ${GridAreas.Right}" "${GridAreas.Footer} ${GridAreas.Footer}"`,
          gridTemplateColumns: isSplitPane ? '1fr 1fr' : '4fr 6fr'
        }}
      >
        {children}
      </Box>
    </VerticalPositionProvider>
  )
}

// --------------------------------------------------------
// LeftPane
export type LeftPaneProps = {
  /**
   * Children to render in the nav
   */
  children?: React.ReactNode
} & BoxProps
const StyledLeftPane = styled(Box)<LeftPaneProps & SxProp>`
  display: flex;
  flex-basis: 100%;
  background-color: ${themeGet('colors.elevation.surface.default.[default]')};
  grid-area: ${GridAreas.Left};
  ${sx}
`
/**
 * Left pane for Rule builder layout. Must be rendered inside of a Rule Builder
 */
const LeftPane = ({children, ...props}: LeftPaneProps) => {
  const {setContainerRef} = useCenterY()

  return (
    <StyledLeftPane {...props} ref={setContainerRef}>
      {children}
    </StyledLeftPane>
  )
}

// --------------------------------------------------------
// RightPane
export type RightPaneProps = {
  /**
   * Children to render in the nav
   */
  children?: React.ReactNode
} & BoxProps
const StyledRightPane = styled(Box)<RightPaneProps & SxProp>`
  background-color: ${themeGet('colors.elevation.surface.default.[default]')};
  height: 100%;
  grid-area: ${GridAreas.Right};
  ${sx}
`
/**
 * Right pane for Rule builder layout. Must be rendered inside of a Rule Builder
 */
const RightPane = ({children, ...props}: RightPaneProps) => {
  return <StyledRightPane {...props}>{children}</StyledRightPane>
}

// --------------------------------------------------------
// Header
export type HeaderProps = {
  /**
   * Children to render in the header
   */
  children?: React.ReactNode
  /**
   * Whether to hide the header
   */
  hidden?: boolean
} & BoxProps
const StyledHeader = styled(Box)<HeaderProps & SxProp>`
  display: flex;
  background-color: ${themeGet('colors.elevation.surface.default.[default]')};
  width: 100%;
  grid-area: ${GridAreas.Header};
  @media (min-width: ${themeGet('breakpoints.2')}) {
  }
  ${sx}
`
/**
 * Header for Rule builder layout
 */
const Header = ({children, hidden = false, ...props}: HeaderProps) => {
  return <StyledHeader {...props}>{children}</StyledHeader>
}

// --------------------------------------------------------
// Footer
export type FooterProps = {
  /**
   * A unique label for the rendered contentinfo landmark
   */
  'aria-label'?: React.AriaAttributes['aria-label']
  /**
   * An id to an element which uniquely labels the rendered contentinfo landmark
   */
  'aria-labelledby'?: React.AriaAttributes['aria-labelledby']
  /**
   * Children to render in the footer
   */
  children?: React.ReactNode
  /**
   * Whether to hide the footer
   */
  hidden?: boolean
} & BoxProps
const StyledFooter = styled(Box)<FooterProps & SxProp>`
  display: flex;
  background-color: ${themeGet('colors.elevation.surface.default.[default]')};
  width: 100%;
  grid-area: ${GridAreas.Footer};
  @media (min-width: ${themeGet('breakpoints.2')}) {
  }
  ${sx}
`
/**
 * Footer for a RuleBuilder Layout. Must be rendered inside of a Rule Builder
 */
const Footer = ({children, hidden = false, ...props}: FooterProps) => {
  return <StyledFooter {...props}>{children}</StyledFooter>
}

RuleBuilderLayout.displayName = 'RuleBuilderLayout'

export default Object.assign(RuleBuilderLayout, {
  Header,
  LeftPane,
  RightPane,
  Footer,
  GridAreas
})
